import {
    AggregateFunctions,
    AggregateQuery,
    ApiQueryEndpoints,
    FilterMetadata,
    QueryResponse,
} from "~/modules/filters/types";
import { DEFAULT_ASSET_TIMESERIES_QUERY } from "../../consts";
import { TimeSeriesItem } from "../../types";
import { TimeSeriesChart } from "~/modules/visualization/components/TimeSeriesChartV3";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { AssetAggregateQueryInput } from "~/modules/filters/components/AssetAggregateQueryInput";
import {
    ApiQueryFetcher,
    ApiQueryStateManager,
    ApiSearchLayout,
} from "~/modules/filters/components/ApiSearchLayout";
import { ExportTableNames } from "~/modules/downloads/types";
import { SearchControlModes } from "~/modules/filters/components/FilterControls/types";
import { ColumnSpanConfig } from "~/types";
import { memo, useMemo } from "react";

// // // //

function VisualizationItem(props: {
    title: string;
    metrics: TimeSeriesItem[];
    showLegend?: boolean;
    showNavigator?: boolean;
    colSpans?: ColumnSpanConfig;
}) {
    const {
        metrics,
        title,
        colSpans = { sm: 12, md: 12, lg: 8, xl: 8, xxl: 8 },
    } = props;

    return (
        <VisualizationCard
            title={title}
            wrapperClassNameOverride=""
            watermark="center-30pct"
            colSpans={colSpans}
        >
            <TimeSeriesChart
                metrics={metrics}
                area={true}
                showTotal={true}
                height={350}
                legend={{
                    itemWidth: 125,
                    enabled: true,
                    layout: "vertical",
                    align: "right",
                    verticalAlign: "top",
                }}
            />
        </VisualizationCard>
    );
}

// // // //

const TimeseriesVisualizationGrid = memo(
    function TokenTimeseriesVisualizationGrid(props: {
        results: TimeSeriesItem[];
    }) {
        const groups = useMemo(() => {
            return Object.entries(
                props.results.reduce(
                    (all: Record<string, TimeSeriesItem[]>, m) => {
                        if (all[m.measure.name]) {
                            all[m.measure.name].push(m);
                            return all;
                        }
                        all[m.measure.name] = [m];
                        return all;
                    },
                    {},
                ),
            );
        }, [props.results]);

        return (
            <VisualizationGrid className="mb-4">
                {groups.map((data) => {
                    return (
                        <VisualizationItem
                            key={data[0]}
                            title={data[0]}
                            colSpans={{
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            }}
                            metrics={data[1] as TimeSeriesItem[]}
                        />
                    );
                })}
            </VisualizationGrid>
        );
    },
    (prev, next) => {
        // Value-based equality on results so the charts only re-render
        // when the underlying data actually changes (not on every
        // internalQuery edit that causes the parent render prop to re-run).
        if (prev.results === next.results) return true;
        if (prev.results.length !== next.results.length) return false;
        return JSON.stringify(prev.results) === JSON.stringify(next.results);
    },
);

// // // //

export function AssetTimeseriesList(props: {
    listQueryResponse: QueryResponse<TimeSeriesItem>;
    filterMetadata: FilterMetadata[];
}) {
    const { listQueryResponse, filterMetadata } = props;

    // // // //

    return (
        <ApiQueryStateManager
            // syncUrl
            defaultFilters={DEFAULT_ASSET_TIMESERIES_QUERY}
        >
            {({ appliedQuery, internalQuery, resetQuery }) => (
                <ApiQueryFetcher
                    appliedQuery={appliedQuery.query}
                    internalQuery={internalQuery.query}
                    endpoint={ApiQueryEndpoints.AssetTimeseriesV4}
                    initialState={listQueryResponse}
                >
                    {(state) => {
                        // Group results by measure for TimeSeries visualizations
                        const groups = Object.entries(
                            state.appliedQueryReq.data.results.reduce(
                                (all: any, m: TimeSeriesItem) => {
                                    if (all[m.measure.name]) {
                                        all[m.measure.name].push(m);
                                        return all;
                                    }
                                    all[m.measure.name] = [m];
                                    return all;
                                },
                                {},
                            ),
                        );

                        return (
                            <ApiSearchLayout
                                // className={""}
                                filterMetadata={filterMetadata}
                                onResetQuery={() => {
                                    resetQuery();
                                    // TODO - reset query state here, from ApiQueryStateManager
                                }}
                                // NOTE - the `export` feature needs a mechanism to define the filename for each layout
                                // i.e. any asset/asset-summary exports will end up having the same filename without
                                appliedQuery={appliedQuery}
                                internalQuery={internalQuery}
                                queryFetcherState={state}
                                controls={{
                                    exportData: {
                                        enabled: true,
                                        exportTableName:
                                            ExportTableNames.asset_timeseries_v4,
                                    },
                                    search: {
                                        enableBasic: false,
                                        enablePro: true,
                                        mode: SearchControlModes.Pro,
                                    },
                                    apiTools: {
                                        enabled: true,
                                        endpoint:
                                            ApiQueryEndpoints.AssetTimeseriesV4,
                                    },
                                    queryExamples: {
                                        items: [],
                                        onSelectItem: (selectedExample) => {
                                            internalQuery.update(
                                                selectedExample.query,
                                            );
                                        },
                                    },
                                    sharing: {
                                        enabled: true,
                                        url: appliedQuery.url,
                                    },
                                }}
                            >
                                <AssetAggregateQueryInput
                                    value={{
                                        groupBy: appliedQuery.query.aggregate
                                            ?.groupBy as string,
                                        aggregateFunction: appliedQuery.query
                                            .aggregate
                                            ?.aggregateFunction as AggregateFunctions,
                                    }}
                                    onChange={(
                                        updatedAggregateQuery: AggregateQuery,
                                    ) => {
                                        appliedQuery.update({
                                            ...appliedQuery.query,
                                            aggregate: updatedAggregateQuery,
                                        });
                                        internalQuery.update({
                                            ...internalQuery.query,
                                            aggregate: updatedAggregateQuery,
                                        });
                                    }}
                                />

                                <TimeseriesVisualizationGrid
                                    results={state.appliedQueryReq.data.results}
                                />
                            </ApiSearchLayout>
                        );
                    }}
                </ApiQueryFetcher>
            )}
        </ApiQueryStateManager>
    );
}
